import { match, P } from 'ts-pattern';
import { zfd } from 'zod-form-data';
import { crewClaimContext } from '~/app/features/crew/crewClaimMiddleware';
import {
  crewOrderContext,
  Order,
  orderContext,
} from '~/app/features/order-middleware/orderMiddleware';
import { shippingClaimContext } from '~/app/features/shipping-claim/new-claim/shippingClaimMiddleware';
import { settingsContext } from '~/app/settingsMiddleware';
import { ClaimType } from '~/utils/constants';
import {
  makeAction,
  makeLoader,
  matchShippingProtectionPath,
  redirect,
} from '~/utils/routing';
import { checkUsPostalCode, customerInfoSchema } from '~/utils/schemas';
import { isAddressPhoneRequired } from '~/utils/theme';

const pickCustomerInfo = (order: Order) => ({
  // If the order is a gift return, the email should be undefined so user has to enter it
  email: order.isGiftReturn ? undefined : order.email,
  firstName: order.shippingAddress?.firstName,
  lastName: order.shippingAddress?.lastName,
  address: order.shippingAddress,
});

export const { loader, useLoaderData } = makeLoader(({ request, context }) =>
  match(matchShippingProtectionPath(request))
    .with(P.nullish, () => {
      const settings = context.get(settingsContext);
      const crewOrder = context.get(crewOrderContext);
      const crewClaim = context.get(crewClaimContext);

      return {
        isShippingProtectionClaim: false,
        customerInfo: crewClaim.customerInfo ?? pickCustomerInfo(crewOrder),
        requirePhone: isAddressPhoneRequired(
          settings.theme,
          crewClaim.claimType,
        ),
      };
    })
    .with(P.nonNullable, () => {
      const order = context.get(orderContext);
      const shippingProtectionClaim = context.get(shippingClaimContext);

      return {
        customerInfo:
          shippingProtectionClaim.customerInfo ?? pickCustomerInfo(order),
        requirePhone: false,
        isShippingProtectionClaim: true,
      };
    })
    .exhaustive(),
);

type ActionReturnType = ReturnType<Parameters<typeof makeAction>[0]>;
type ActionFnArgs = Parameters<Parameters<typeof makeAction>[0]>;

const handleShippingProtectionRoute = (
  { formData }: ActionFnArgs[0],
  appStore: ActionFnArgs[1],
) =>
  match(
    zfd
      .formData(
        customerInfoSchema.omit({ email: true }).check(checkUsPostalCode),
      )
      .safeParse(formData),
  )
    .returnType<ActionReturnType>() // help assert the false case is inferred as ActionResult
    .with({ success: true }, ({ data }) => {
      appStore.dispatch(
        appStore.actions.shippingProtectionClaim.setCustomerInfo(data),
      );

      return redirect('../review');
    })
    .with({ success: false }, ({ error }) => ({
      ok: false,
      message: error.message,
    }))
    .exhaustive();

const handleCrewClaimRoute = (
  { context, formData }: ActionFnArgs[0],
  appStore: ActionFnArgs[1],
) => {
  const settings = context.get(settingsContext);
  const crewClaim = context.get(crewClaimContext);

  const schema = customerInfoSchema
    .extend({
      address: customerInfoSchema.shape.address.required({
        phone:
          isAddressPhoneRequired(settings.theme, crewClaim.claimType) ||
          undefined,
      }),
    })
    .check(checkUsPostalCode);

  return match(zfd.formData(schema).safeParse(formData))
    .returnType<ActionReturnType>() // help assert the false case is inferred as ActionResult
    .with({ success: true }, ({ data }) => {
      appStore.dispatch(appStore.actions.crewClaim.setCustomerInfo(data));
      const nextStep =
        crewClaim.claimType === ClaimType.warranty
          ? `../../warranty-review`
          : `../../return-methods`;
      return redirect(nextStep);
    })
    .with({ success: false }, ({ error }) => ({
      ok: false,
      message: error.message,
    }))
    .exhaustive();
};

export const { action, useActionResult } = makeAction((...args) =>
  matchShippingProtectionPath(args[0].request)
    ? handleShippingProtectionRoute(...args)
    : handleCrewClaimRoute(...args),
);
